import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getTickets, createWebSocket } from '../services/api';
import './DisplayScreen.css';

function DisplayScreen() {
  const { facultyId } = useParams();
  const [tickets, setTickets] = useState([]);
  const announcementQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const wsRef = useRef(null);
  const reconnectDelay = useRef(1000);

  const loadTickets = useCallback(async () => {
    try {
      const res = await getTickets({ status: 'called,serving', today: true });
      const list = res.data.results || res.data;
      setTickets(list);
    } catch (e) {
      console.error('Failed to load tickets:', e);
    }
  }, []);

  const playNextAnnouncement = useCallback(() => {
    if (announcementQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }
    isPlayingRef.current = true;
    const url = announcementQueueRef.current.shift();

    // Play notification ding first
    const ding = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARAAIAIAAAAAgAAEAZGF0YQAAAAA=');
    ding.volume = 0.5;
    ding.play().catch(() => {});

    setTimeout(() => {
      const audio = new Audio(url);
      audio.volume = 1.0;
      audio.playbackRate = 1.5;
      audio.onended = () => setTimeout(playNextAnnouncement, 800);
      audio.onerror = () => setTimeout(playNextAnnouncement, 200);
      audio.play().catch(() => setTimeout(playNextAnnouncement, 200));
    }, 700);
  }, []);

  const announceTicket = useCallback((ticket) => {
    if (!ticket?.ticket_number) return;
    const operator = ticket.counter_number || ticket.assigned_staff?.counter_number || '1';
    const url = `/tts/announcement/?ticket_number=${ticket.ticket_number}&operator_number=${operator}`;
    announcementQueueRef.current.push(url);
    if (!isPlayingRef.current) playNextAnnouncement();
  }, [playNextAnnouncement]);

  const connectWS = useCallback(() => {
    const id = facultyId === 'all' ? 'all' : facultyId;
    if (wsRef.current) { try { wsRef.current.close(); } catch(e){} }

    const ws = createWebSocket(id, 'display');
    wsRef.current = ws;

    ws.onopen = () => { reconnectDelay.current = 1000; };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'display_update' && data.event === 'ticket_called') {
          announceTicket(data.ticket);
          loadTickets();
        } else if (data.type === 'display_update') {
          loadTickets();
        }
      } catch(e) {}
    };

    ws.onclose = () => {
      setTimeout(() => {
        reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
        connectWS();
      }, reconnectDelay.current);
    };
  }, [facultyId, announceTicket, loadTickets]);

  useEffect(() => {
    loadTickets();
    connectWS();
    const interval = setInterval(loadTickets, 15000);
    return () => {
      clearInterval(interval);
      if (wsRef.current) { try { wsRef.current.close(); } catch(e){} }
    };
  }, [loadTickets, connectWS]);

  return (
    <div className="display-screen">
      <div className="display-header">
        <h1>Navbat tizimi</h1>
      </div>
      <div className="display-content">
        {tickets.length === 0 ? (
          <div className="no-tickets-display">
            <h2>Chaqirilgan navbatlar yo'q</h2>
            <p>Iltimos kuting...</p>
          </div>
        ) : (
          <div className="ticket-display-grid">
            {tickets.map((t) => (
              <div key={t.id} className={`ticket-display-card ${t.status === 'called' ? 'ticket-called' : 'ticket-serving'}`}>
                <div className="ticket-display-number">{t.ticket_number}</div>
                <div className="ticket-display-info">
                  <span className="ticket-display-operator">
                    Operator: {t.counter_number || 'N/A'}
                  </span>
                  <span className={`ticket-display-status status-${t.status}`}>
                    {t.status === 'called' ? 'Chaqirildi' : 'Xizmatda'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DisplayScreen;
